/**
 * Transition Rules: EnergyPlus 9.3 to 9.4
 */

import type { VersionTransition, ObjectTransitionRule } from '../types';
import { createCopyMappings, createShiftedCopyMappings } from '../types';

export function createTransition_9_3_to_9_4(): VersionTransition {
  const objectRules = new Map<string, ObjectTransitionRule>();
  const variableMappings = new Map<string, string>();
  const deletedObjects = new Set<string>();
  const renamedObjects = new Map<string, string>();

  // Construction:InternalSource - Insert new field at position 6 with default '0.0'
  objectRules.set('CONSTRUCTION:INTERNALSOURCE', {
    sourceClass: 'Construction:InternalSource',
    targetClass: 'Construction:InternalSource',
    fieldMappings: [
      ...createCopyMappings(0, 5), // Fields 0-4
      { sourceIndex: -1, targetIndex: 5, type: 'add_default', defaultValue: '0.0' },
      ...createShiftedCopyMappings(5, 6, 20),
    ],
  });

  // EnergyManagementSystem:Actuator - Transform field 4 (Actuator Type Name)
  objectRules.set('ENERGYMANAGEMENTSYSTEM:ACTUATOR', {
    sourceClass: 'EnergyManagementSystem:Actuator',
    targetClass: 'EnergyManagementSystem:Actuator',
    fieldMappings: [
      ...createCopyMappings(0, 3), // Fields 0-2
      {
        sourceIndex: 3,
        targetIndex: 3,
        type: 'transform',
        transform: (value) => {
          const upper = value.toUpperCase().replace(/\s+/g, '');
          if (upper === 'ELECTRICPOWERLEVEL') return 'Electricity Rate';
          if (upper === 'GASPOWERLEVEL') return 'NaturalGas Rate';
          return value;
        },
      },
      ...createShiftedCopyMappings(4, 4, 10),
    ],
  });

  // Output:DebuggingData - Convert numeric values to Yes/No
  objectRules.set('OUTPUT:DEBUGGINGDATA', {
    sourceClass: 'Output:DebuggingData',
    targetClass: 'Output:DebuggingData',
    fieldMappings: [
      {
        sourceIndex: 0,
        targetIndex: 0,
        type: 'transform',
        transform: (value) => value === '1' ? 'Yes' : 'No',
      },
      {
        sourceIndex: 1,
        targetIndex: 1,
        type: 'transform',
        transform: (value) => value === '1' ? 'Yes' : 'No',
      },
    ],
  });

  // PerformancePrecisionTradeoffs - Mode05 → Mode06
  objectRules.set('PERFORMANCEPRECISIONTRADEOFFS', {
    sourceClass: 'PerformancePrecisionTradeoffs',
    targetClass: 'PerformancePrecisionTradeoffs',
    fieldMappings: [
      ...createCopyMappings(0, 2), // Fields 0-1
      {
        sourceIndex: 2,
        targetIndex: 2,
        type: 'transform',
        transform: (value) => value.toUpperCase() === 'MODE05' ? 'Mode06' : value,
      },
      ...createShiftedCopyMappings(3, 3, 10),
    ],
  });

  // ZoneHVAC:LowTemperatureRadiant:VariableFlow - Insert 3 new fields
  objectRules.set('ZONEHVAC:LOWTEMPERATURERADIANT:VARIABLEFLOW', {
    sourceClass: 'ZoneHVAC:LowTemperatureRadiant:VariableFlow',
    targetClass: 'ZoneHVAC:LowTemperatureRadiant:VariableFlow',
    fieldMappings: [
      ...createCopyMappings(0, 4), // Fields 0-3
      { sourceIndex: -1, targetIndex: 4, type: 'add_default', defaultValue: 'ConvectionOnly' },
      { sourceIndex: 4, targetIndex: 5, type: 'copy' },
      { sourceIndex: -1, targetIndex: 6, type: 'add_default', defaultValue: '0.016' },
      ...createShiftedCopyMappings(5, 7, 4),
      { sourceIndex: -1, targetIndex: 10, type: 'add_default', defaultValue: 'HalfFlowPower' },
      ...createShiftedCopyMappings(9, 11, 30),
    ],
  });

  // ZoneHVAC:LowTemperatureRadiant:Electric - Insert field at position 10
  objectRules.set('ZONEHVAC:LOWTEMPERATURERADIANT:ELECTRIC', {
    sourceClass: 'ZoneHVAC:LowTemperatureRadiant:Electric',
    targetClass: 'ZoneHVAC:LowTemperatureRadiant:Electric',
    fieldMappings: [
      ...createCopyMappings(0, 9), // Fields 0-8
      { sourceIndex: -1, targetIndex: 9, type: 'add_default', defaultValue: 'HalfFlowPower' },
      ...createShiftedCopyMappings(9, 10, 20),
    ],
  });

  // ZoneHVAC:LowTemperatureRadiant:ConstantFlow - Insert 3 new fields
  objectRules.set('ZONEHVAC:LOWTEMPERATURERADIANT:CONSTANTFLOW', {
    sourceClass: 'ZoneHVAC:LowTemperatureRadiant:ConstantFlow',
    targetClass: 'ZoneHVAC:LowTemperatureRadiant:ConstantFlow',
    fieldMappings: [
      ...createCopyMappings(0, 4), // Fields 0-3
      { sourceIndex: -1, targetIndex: 4, type: 'add_default', defaultValue: 'ConvectionOnly' },
      { sourceIndex: 4, targetIndex: 5, type: 'copy' },
      { sourceIndex: -1, targetIndex: 6, type: 'add_default', defaultValue: '0.016' },
      ...createShiftedCopyMappings(5, 7, 4),
      { sourceIndex: -1, targetIndex: 10, type: 'add_default', defaultValue: '0.8' },
      ...createShiftedCopyMappings(9, 11, 30),
    ],
  });

  return {
    fromVersion: { major: 9, minor: 3 },
    toVersion: { major: 9, minor: 4 },
    objectRules,
    variableMappings,
    deletedObjects,
    renamedObjects,
  };
}
